/**
 * Referral Link Generator Component
 * Generate and copy affiliate referral links
 */

import { useState } from 'react';
import { affiliateApi } from '../../api/affiliate';

interface ReferralLinkGeneratorProps {
  affiliateCode: string;
}

export default function ReferralLinkGenerator({ affiliateCode }: ReferralLinkGeneratorProps) {
  const [customUrl, setCustomUrl] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    if (!customUrl.trim()) {
      return;
    }

    try {
      setLoading(true);
      const response = await affiliateApi.generateCustomLink(customUrl);
      setGeneratedLink(response.affiliateUrl);
    } catch (error: any) {
      alert(error.message || 'Failed to generate link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (link: string) => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const baseUrl = window.location.origin;

  const predefinedLinks = [
    {
      label: 'Homepage',
      url: `${baseUrl}?ref=${affiliateCode}`,
      description: 'Link to homepage',
    },
    {
      label: 'Products',
      url: `${baseUrl}/products?ref=${affiliateCode}`,
      description: 'Link to product catalog',
    },
    {
      label: 'Sign Up',
      url: `${baseUrl}/register?ref=${affiliateCode}`,
      description: 'Direct registration link',
    },
  ];

  return (
    <div className="bg-slate-800/80 rounded-lg border border-slate-700/50 p-6">
      <h2 className="text-xl font-semibold text-white mb-4">Referral Links</h2>

      {/* Predefined Links */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-slate-400 mb-3">Quick Links</h3>
        <div className="space-y-3">
          {predefinedLinks.map((link, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-slate-700/50/50 rounded-lg"
            >
              <div className="flex-1 mr-4">
                <p className="text-white font-medium mb-1">{link.label}</p>
                <p className="text-sm text-slate-400 break-all">{link.url}</p>
              </div>
              <button
                onClick={() => handleCopy(link.url)}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition flex items-center space-x-2 whitespace-nowrap"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span>Copy</span>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Link Generator */}
      <div>
        <h3 className="text-sm font-medium text-slate-400 mb-3">
          Generate Custom Link
        </h3>
        <div className="flex space-x-3">
          <input
            type="url"
            value={customUrl}
            onChange={(e) => setCustomUrl(e.target.value)}
            placeholder="Enter any URL (e.g., https://example.com/page)"
            className="flex-1 bg-slate-700/50 text-white rounded-lg px-4 py-2 border border-gray-600 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
          />
          <button
            onClick={handleGenerate}
            disabled={loading || !customUrl.trim()}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {loading ? 'Generating...' : 'Generate'}
          </button>
        </div>

        {generatedLink && (
          <div className="mt-4 p-4 bg-slate-700/50/50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-4">
                <p className="text-sm text-slate-400 mb-1">Generated Link:</p>
                <p className="text-white break-all">{generatedLink}</p>
              </div>
              <button
                onClick={() => handleCopy(generatedLink)}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition flex items-center space-x-2"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span>Copy</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Copy Notification */}
      {copied && (
        <div className="fixed bottom-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-fade-in">
          <svg
            className="w-5 h-5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M5 13l4 4L19 7" />
          </svg>
          <span>Link copied to clipboard!</span>
        </div>
      )}

      {/* Help Text */}
      <div className="mt-4 p-4 bg-orange-900/20 border border-orange-700 rounded-lg">
        <p className="text-sm text-orange-300">
          <strong>Tip:</strong> Share these links on social media, your website, or via email
          to earn commissions when people sign up through your referral link.
        </p>
      </div>
    </div>
  );
}
